import ExportArtifactOption from "../../ExportArtifactOption";
import { styled } from "@linaria/react";
import * as io from "io-ts";
import React, { useMemo } from "react";
import Button from "@ui/Button";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import RadioButton from "@ui/RadioButton/index";
import formatNumber from "@ui/i18n/formatNumber";
import cx from "classnames";

import TableEditableCell, { TableInputControl, TableSelectControl } from "@ui/Table/EditableCell";
import TableForm from "@ui/Table/Form";
import createGlobalState from "@ui/lib/createGlobalState";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import useSortedData from "../../../../../../lib/hooks/useSortedData";
import t from "../../../../../../i18n";
import { literalUnion } from "diagnostics-lib/io";
import type { SortableValue } from "diagnostics-lib/comparator";
import { filter } from "diagnostics-lib/tuple";
import InlineMenu from "@ui/InlineMenu";
import Link from "@ui/Link";
import Option, { OptionGroup } from "@ui/Option";
import createIOSortType from "../../../../../../lib/createIOSortType";
import { nonEmptyStringValidation } from "../../../../formValidationFunctions";
import Card from "@ui/Card";
import TableCard from "@ui/Table/Card";
import { color, fonts } from "@ui";
import Switch from "@ui/Switch";
import type { Failure, Setup } from "../../../../../../api/useSetups";
import { useQuantities } from "../../../../../../api/useQuantities";
import { useCanUserDo, useCanUserDoOnProject } from "../../../../../canUserDo";
import { WarningComponent } from "../../Simulation/SimulationStyle";
import useConfirmModal from "@ui/Modal/useConfirmModal";
import { usePut } from "../../../../../../api/useCall";
import useDirtyForm from "@ui/Table/useDirtyForm";
import Badge from "@ui/Badge";
import classNames from "classnames";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import ButtonIconV2 from "@ui/ButtonIconV2";
import ReparabilityTag from "./Reparability";
import { buildOptions } from "@ui/Filter";
import EquipmentSelect from "../../../EquipmentSelect";
import SuspectedElementSelect from "../../../SuspectedElementSelect";
import SystemSelect from "../../../SystemSelect";
import EquipmentTypeSelect from "../../../EquipmentTypeSelect";
import DegradationTypeSelect from "../../../DegradationTypeSelect";
import { useProjectCategories } from "../../../../../../api/useProjectCategories";
import CategorySelect from "../../../CategorySelect";
import { DeleteBinLine, More2Fill, PencilLine, UploadLine } from "@ui/IconV2/react";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import BlankStateV2 from "@ui/BlankStateV2";
import { getFailureColor } from "../../../../useFailureColor";
import type { Project } from "../../../../../../api/useProjects";
import Selector from "@ui/Selector";
import FilterSelect from "@ui/Filter/FilterSelect";
import Reset from "@ui/Filter/Reset";
import FaultFilterTable from "./FaultFilterTable";
import { css } from "@linaria/core";
import { useTranslations } from "../../../../../../api/useTranslations";
import { useSetupActiveAnalysisCount } from "../../../../../../api/useActiveAnalysesStream";
import {
  TableCardWithoutBorder,
  TableHeaderNoPaddingT,
} from "@ui/Table/_styles/TableCardWithoutBorder";
import Menu from "@ui/Menu";
import { MAX_FAULT_UNIT_LENGTH } from "./optionsValidation";
import type { Reparability } from "../../../../../../api/Reparability";
import { reparabilities } from "../../../../../../api/Reparability";
import { FaultSignatureComparisonModal } from "./FaultSignatureComparison";
import { MAX_COMPARABLE_FAULTS, MIN_COMPARABLE_FAULTS } from "./faultSignatureComparisonModel";
import { spacings } from "@ui/Theme";
import Checkbox from "@ui/Checkbox";

export type FailuresModal = "failuresImport" | "failuresDelete";
const FIRST_STICKY_COLUMN_WIDTH = 220;
const OBERVABLE_CN = "observable";
const DISABLED_CN = "disabled";
const QOI_CN = "is-qoi";

const useSelectorDiagnosis = createGlobalState({
  storage: "session",
  key: "settingSelectorDiagnosis",
  type: io.union([io.literal("matrix"), io.literal("filter")]),
  defaultValue: "matrix",
  scoped: true,
});
type SelectorMenu = "matrix" | "filter";
type SelectorMenuOptions = {
  label: string;
  value: SelectorMenu;
  disabled?: boolean;
  title?: string;
}[];

interface FaultComparisonButtonProps {
  mode: SelectorMenu;
  onClick: () => void;
  selectedFaultCount: number;
}

export const FaultComparisonButton = ({
  mode,
  onClick,
  selectedFaultCount,
}: FaultComparisonButtonProps) => {
  const enabled =
    mode === "matrix" &&
    selectedFaultCount >= MIN_COMPARABLE_FAULTS &&
    selectedFaultCount <= MAX_COMPARABLE_FAULTS;
  const disabledReason =
    mode === "matrix"
      ? selectedFaultCount < MIN_COMPARABLE_FAULTS
        ? t("setup.diagnosis.failure.compare.disabled.minimum")
        : t("setup.diagnosis.failure.compare.disabled.maximum")
      : t("setup.diagnosis.failure.compare.disabled.mode");

  if (enabled) {
    return (
      <Button variant="primary" onClick={onClick}>
        {t("setup.diagnosis.failure.compare.button")}
      </Button>
    );
  }

  return (
    <TooltipV2 placement="top" overlay={<OverlayWrapper text={disabledReason} />}>
      <span>
        <Button variant="primary" disabled>
          {t("setup.diagnosis.failure.compare.button")}
        </Button>
      </span>
    </TooltipV2>
  );
};

interface FailureComparisonCheckboxProps {
  checked: boolean;
  onToggle: () => void;
}

export const FailureComparisonCheckbox = ({
  checked,
  onToggle,
}: FailureComparisonCheckboxProps) => (
  <Checkbox
    className={failureSelectionInput}
    checked={checked}
    onChange={onToggle}
    onClick={event => event.stopPropagation()}
  />
);

interface Props {
  failures: Failure[];
  project: Project;
  setup: Setup;
  editable: boolean;
  editedDataKey: string | undefined;
  setEditedDataKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  openModal: (name: FailuresModal) => void;
  setups: Setup[];
}

const useDisplayDisabled = createGlobalState({
  storage: "session",
  key: "displayDisabledFailure",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

const useDisplayQuantitiesNotUsed = createGlobalState({
  storage: "session",
  key: "displayNotUsedFailure",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

const columns = [
  "displayName",
  "technicalName",
  "description",
  "degradationType",
  "failureCategory",
  "reparability",
  "system",
  "equipments",
  "equipmentType",
  "suspectedElements",
  "unit",
  "min",
  "max",
  "amplitude",
  "separator",
] as const;
const defaultColumns: Column[] = [
  "displayName",
  "description",
  "degradationType",
  "failureCategory",
  "reparability",
  "system",
  "equipments",
  "equipmentType",
  "suspectedElements",
  "unit",
  "min",
  "max",
  "amplitude",
];
type Column = (typeof columns)[number];

const sortableColumns = filter(columns, ["separator"] as const);
type SortableColumn = (typeof sortableColumns)[number];

const useColumns = createGlobalState({
  storage: "session",
  key: "setupHiddenFailuresColumns",
  type: io.array(literalUnion(columns)),
  defaultValue: defaultColumns,
  scoped: true,
});

const useFailureSearch = createGlobalState({
  storage: "session",
  key: "failureSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});

const useObservableSelection = createGlobalState({
  storage: "session",
  key: "faultMatrixObservableSelection",
  type: io.array(io.string),
  defaultValue: [],
  scoped: true,
});

export type EditableFailure = Failure & {
  edited?: boolean;
};

interface ConfirmToggleFailureProps {
  enable: boolean;
  failureName: string;
  analysesImpactedCount: number;
}

const ConfirmToggleFailure = ({
  enable,
  failureName,
  analysesImpactedCount,
}: ConfirmToggleFailureProps) => (
  <Wrapper>
    <MainText>{t("setup.diagnosis.observable.confirm.toggle.body", enable, failureName)}</MainText>
    <WarningComponent impactedAnalysisCount={analysesImpactedCount} />
  </Wrapper>
);

const getLabel = (column: Column) => {
  switch (column) {
    case "displayName":
      return t("setup.paragraphs.failures.table.column.displayName");
    case "technicalName":
      return t("setup.paragraphs.failures.table.column.technicalName");
    case "degradationType":
      return t("setup.paragraphs.failures.table.column.degradationType");
    case "failureCategory":
      return t("setup.paragraphs.failures.table.column.category");
    case "reparability":
      return t("setup.paragraphs.failures.table.column.reparability");
    case "system":
      return t("setup.paragraphs.failures.table.column.system");
    case "equipments":
      return t("setup.paragraphs.failures.table.column.equipments");
    case "equipmentType":
      return t("setup.paragraphs.failures.table.column.equipmentType");
    case "suspectedElements":
      return t("setup.paragraphs.failures.table.column.suspectedElements");
    case "unit":
      return t("setup.paragraphs.failures.table.column.unit");
    case "min":
      return t("setup.paragraphs.failures.table.column.min");
    case "max":
      return t("setup.paragraphs.failures.table.column.max");
    case "amplitude":
      return t("setup.paragraphs.failures.table.column.magnitude");
    case "description":
      return t("setup.paragraphs.failures.table.column.description");
    case "separator":
      return t("setup.paragraphs.failures.table.column.separator");
    default:
      throw new Error(column satisfies never);
  }
};

const computeRowKey = (failure: Failure) => failure.id;

const MAX_DESCRIPTION_LENGTH = 80;
const MAX_DISPLAY_NAME_LENGTH = 100;

const SORT_SELECTORS: {
  [key in SortableColumn]: (d: Failure) => SortableValue | undefined | null;
} = {
  displayName: d => d.displayName,
  technicalName: d => d.name,
  degradationType: d => d.degradationTypeId,
  failureCategory: d => d.failureCategoryId,
  reparability: d => d.reparability,
  system: q => q.systemId,
  equipments: d => d.equipments?.[0]?.equipment.name,
  equipmentType: d => d.equipmentTypeId,
  suspectedElements: d => d.suspectedElements?.[0]?.suspectedElement.name,
  unit: d => d.unit,
  max: d => d.max,
  min: d => d.min,
  amplitude: d => d.amplitude,
  description: d => d.description,
};

const useSort = createGlobalState({
  storage: "session",
  key: "setupFailuresSort",
  type: createIOSortType(sortableColumns),
  defaultValue: {
    column: "displayName",
    direction: "asc",
  },
  scoped: true,
});

const FormSignature = io.partial({
  description: io.string, // string
  unit: io.string, // non-empty string

  system: io.string,
  equipmentType: io.string,
  equipments: io.array(io.string),
  suspectedElements: io.array(io.string),
  degradationType: io.string,
  failureCategory: io.string,
  reparability: io.string,
  displayName: io.string,
});

type FormSignature = io.TypeOf<typeof FormSignature>;

const FailuresTable = ({
  project,
  openModal,
  setup,
  setups,
  editedDataKey,
  setEditedDataKey,
}: Props) => {
  const translations = useTranslations();
  const activeAnalysisCount = useSetupActiveAnalysisCount(project.id, setup.id);
  const canUserEditSetup = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const canUserModifyGlobalCategories = useCanUserDo("CATEGORY_EDIT");
  const canUserModifyCategories = useCanUserDoOnProject("PROJECT_CATEGORY_EDIT", project.id);

  const requiredVisibleColumns = new Set<Column>(["displayName"]);

  const [option, setOption] = useSelectorDiagnosis({
    scope: setup.id,
  });
  const [displayDisabled, setDisplayDisabled] = useDisplayDisabled({
    scope: setup.id,
  });
  const [displayQuantitiesNotUsed, setDisplayQuantitiesNotUsed] = useDisplayQuantitiesNotUsed({
    scope: setup.id,
  });
  const inputs = setup.diagnosisInputs;
  const failures = setup.failures;

  const uniqueDisplayNameValidation =
    (failures: { id: string; displayName?: string }[], currentId: string) => (value: string) =>
      failures.some(f => f.displayName === value && f.id !== currentId)
        ? t("errors.conflict")
        : undefined;

  const selectorMenuOptions: SelectorMenuOptions = useMemo(() => {
    const nbFilters = failures.reduce((acc, f) => f.filters.length + acc, 0);
    const disabled = nbFilters === 0;
    if (disabled) setOption("matrix");
    return [
      {
        label: t("setup.failures.table.selectors.matrix"),
        value: "matrix",
      },
      {
        label: t("setup.failures.table.selectors.filter"),
        value: "filter",
        disabled,
        ...(disabled ? { tooltipText: t("setup.failures.table.selectors.filter.disabled") } : {}),
      },
    ];
  }, [failures, setOption]);

  // Matrix table
  const optionsReparabilities = useMemo(
    () =>
      buildOptions(
        reparabilities.map(reparability => ({
          label: t("reparability", reparability, translations),
          value: reparability,
        })),
      ),
    [translations],
  );

  const filteredFailures = failures.filter(q => (displayDisabled ? q : q.enabled));

  const handleDisabledChanged = React.useCallback(() => {
    setDisplayDisabled(!displayDisabled);
  }, [setDisplayDisabled, displayDisabled]);

  const handleNotUsedChanged = React.useCallback(() => {
    setDisplayQuantitiesNotUsed(!displayQuantitiesNotUsed);
  }, [setDisplayQuantitiesNotUsed, displayQuantitiesNotUsed]);

  const quantities = useQuantities(project.id);
  const observables = React.useMemo(
    () =>
      inputs
        .filter(input => quantities.some(quantity => quantity.id === input.quantityId))
        .map(input => {
          const quantityFound = quantities.find(q => input.quantityId === q.id);

          if (quantityFound) {
            return {
              id: input.id,
              quantityId: input.quantityId,
              enabled: input.enabled,
              sigma: input.sigma,
              name: quantityFound.name,
              description: quantityFound.description,
              unit: quantityFound.unit,
              filter: {
                min: quantityFound.minMetrology,
                max: quantityFound.maxMetrology,
                maxStd: quantityFound.maxStdMetrology,
              },
            };
          } else {
            throw new Error("it should not happen");
          }
        }),
    [inputs, quantities],
  );

  const [sort, setSort] = useSort({
    scope: `project-${project.id}-setup-${setup.id}/${quantities.map(q => q.id).join("/")}`,
  });

  const [menuOpened, setMenuOpened] = React.useState(false);
  const [selectedFailureIds, setSelectedFailureIds] = React.useState<string[]>([]);
  const [comparisonOpen, setComparisonOpen] = React.useState(false);
  const selectedFaults = React.useMemo(
    () => failures.filter(failure => selectedFailureIds.includes(failure.id)),
    [failures, selectedFailureIds],
  );
  const selectedFaultCount = selectedFaults.length;
  const toggleFailureSelection = React.useCallback((failureId: string) => {
    setSelectedFailureIds(current =>
      current.includes(failureId)
        ? current.filter(id => id !== failureId)
        : [...current, failureId],
    );
  }, []);
  const [visibleColumns, setVisibleColumns] = useColumns({
    scope: `project-${project.id}-setup-${setup.id}`,
  });

  const [selectedObservableIds, setSelectedObservableIds] = useObservableSelection({
    scope: `project-${project.id}-setup-${setup.id}`,
  });
  const observableOptions = React.useMemo(
    () =>
      observables
        .filter(observable =>
          failures[0]?.impacts.some(impact => impact.quantityId === observable.quantityId),
        )
        .map(observable => ({ label: observable.name, value: observable.quantityId }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [observables, failures],
  );
  const activeObservableIds = React.useMemo(
    () => selectedObservableIds.filter(id => observableOptions.some(option => option.value === id)),
    [selectedObservableIds, observableOptions],
  );
  const filteringObservables = activeObservableIds.length > 0;

  const deferredVisibleColumns = React.useDeferredValue(visibleColumns);
  const hidden = React.useCallback(
    (column: Column) =>
      filteringObservables ? column !== "displayName" : !deferredVisibleColumns.includes(column),
    [deferredVisibleColumns, filteringObservables],
  );

  const findDataById = React.useCallback(
    (key: string) => filteredFailures.find(q => q.id === key),
    [filteredFailures],
  );
  const datas: EditableFailure[] = React.useMemo(() => {
    if (editedDataKey === undefined) return filteredFailures;
    return filteredFailures.reduce<EditableFailure[]>((acc, data) => {
      acc.push(data.id === editedDataKey ? { ...data, edited: true } : data);
      return acc;
    }, []);
  }, [editedDataKey, filteredFailures]);

  const [search, setSearch] = useFailureSearch({
    scope: `project-${project.id}-setup-${setup.id}`,
  });

  const searchSelector = React.useCallback((f: EditableFailure) => f.displayName, []);

  const [filteredDatas, dataLoading] = useFilteredDatas(datas, search, searchSelector);

  const sortedFilteredData = useSortedData(
    filteredDatas,
    SORT_SELECTORS[sort.column],
    sort.direction,
  );

  const confirm = useConfirmModal();
  const updateFailures = usePut(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/diagnosis/failures",
  );
  const toggleFailure = React.useCallback(
    (failure: Failure) => async () => {
      const isConfirm = await confirm({
        title: failure.enabled
          ? t("setup.diagnosis.failure.confirm.deactivate.title")
          : t("setup.diagnosis.failure.confirm.activate.title"),
        body: (
          <ConfirmToggleFailure
            enable={failure.enabled}
            failureName={failure.name}
            analysesImpactedCount={activeAnalysisCount}
          />
        ),
      });

      if (isConfirm) {
        await updateFailures({
          routeParams: { projectId: project.id, setupId: setup.id },
          body: [
            {
              ...failure,
              systemId: failure.systemId,
              equipmentTypeId: failure.equipmentTypeId,
              degradationTypeId: failure.degradationTypeId,
              failureCategoryId: failure.failureCategoryId,
              equipmentIds: failure.equipments.map(e => e.equipment.id) ?? [],
              suspectedElementIds: failure.suspectedElements.map(s => s.suspectedElement.id) ?? [],
              reparability: failure.reparability,
              enabled: !failure.enabled,
            },
          ],
        });
      }
    },
    [confirm, activeAnalysisCount, project.id, setup.id, updateFailures],
  );

  const [dirty, setDirty] = useDirtyForm<string>();

  const columnsFromImpacts = failures[0]?.impacts.map(impact => {
    const quantity = quantities.find(q => impact.quantityId === q.id);

    if (quantity) {
      const correspondingObservable = observables.find(obs => obs.quantityId === quantity.id);
      return {
        ...quantity,
        name: quantity.name,
        description: quantity.description,
        unit: quantity.unit,
        enabled: correspondingObservable?.enabled,
      };
    } else {
      throw new Error("it should not happen");
    }
  });

  const notAllQuantitiesUsed =
    columnsFromImpacts && columnsFromImpacts.filter(obs => !obs.enabled).length > 0;

  // #region Equipments
  const equipments = useProjectCategories(project.id, "EQUIPMENT");
  const equipmentNames = React.useCallback(
    (failure: EditableFailure) =>
      (
        failure.equipments
          ?.map(equipment => equipments.find(e => e.id === equipment.equipment.id)?.name ?? null)
          .filter(Boolean) ?? []
      ).join(", "),
    [equipments],
  );
  // #endregion
  // #region Suspected Elements
  const suspectedElements = useProjectCategories(project.id, "SUSPECTED_ELEMENT");
  const suspectedElementNames = React.useCallback(
    (failure: EditableFailure) =>
      suspectedElements
        .filter(se => failure.suspectedElements?.some(el => el.suspectedElement.id === se.id))
        .map(se => se.name)
        .join(", "),
    [suspectedElements],
  );
  // #endregion
  const metadata = React.useMemo(() => {
    const baseMetadata: {
      [key in Exclude<
        Column,
        "name" | "displayName" | "technicalName"
      >]: ColumnMetaData<EditableFailure>;
    } = {
      description: {
        label: getLabel("description"),
        hidden: hidden("description"),
        minWidth: "200px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="description"
            dataKey={f.id}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            renderControl={props => (
              <TableInputControl
                {...props}
                defaultValue={f.description ?? ""}
                setDirty={setDirty}
                placeholder={getLabel("description")}
                maxLength={MAX_DESCRIPTION_LENGTH}
              />
            )}
            showEmptyMark
          >
            {f.description}
          </TableEditableCell>
        ),
      },
      degradationType: {
        label: getLabel("degradationType"),
        hidden: hidden("degradationType"),
        minWidth: "170px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="degradationType"
            dataKey={computeRowKey(f)}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            showEmptyMark
            renderControl={props => (
              <DegradationTypeSelect
                {...props}
                name="degradationType"
                placeholder={getLabel("degradationType")}
                allowDegradationTypeCreation={canUserModifyCategories}
                entry={f}
                setDirty={setDirty}
              />
            )}
          >
            {f.degradationTypeId ?? ""}
          </TableEditableCell>
        ),
        sortable: true,
      },
      failureCategory: {
        label: getLabel("failureCategory"),
        hidden: hidden("failureCategory"),
        minWidth: "250px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="failureCategory"
            dataKey={computeRowKey(f)}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            renderControl={props => (
              <CategorySelect
                {...props}
                name="failureCategory"
                placeholder={getLabel("failureCategory")}
                setDirty={setDirty}
                allowCategoryCreation={canUserModifyGlobalCategories}
                entry={f}
              />
            )}
            showEmptyMark
          >
            {f.failureCategoryId ?? ""}
          </TableEditableCell>
        ),

        sortable: true,
      },
      reparability: {
        label: getLabel("reparability"),
        hidden: hidden("reparability"),
        minWidth: "305px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="reparability"
            dataKey={computeRowKey(f)}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            renderControl={props => (
              <TableSelectControl
                {...props}
                setDirty={setDirty}
                placeholder={getLabel("reparability")}
                defaultValue={
                  f.reparability
                    ? {
                        value: f.reparability,
                        label: t("reparability", f.reparability, translations),
                      }
                    : null
                }
                options={optionsReparabilities}
              />
            )}
            showEmptyMark
          >
            {f.reparability ? (
              <ReparabilityTag reparability={f.reparability} translations={translations} />
            ) : null}
          </TableEditableCell>
        ),
        sortable: true,
      },
      system: {
        label: getLabel("system"),
        hidden: hidden("system"),
        minWidth: "180px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="system"
            dataKey={computeRowKey(f)}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            renderControl={props => (
              <SystemSelect
                {...props}
                ref={props.focusableRef as React.RefObject<HTMLDivElement>}
                name="system"
                placeholder={getLabel("system")}
                setDirty={setDirty}
                allowSystemCreation={canUserModifyGlobalCategories}
                entry={f}
              />
            )}
            showEmptyMark
          >
            {f.systemId ?? ""}
          </TableEditableCell>
        ),
        sortable: true,
      },
      equipments: {
        label: getLabel("equipments"),
        hidden: hidden("equipments"),
        minWidth: "250px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="equipments"
            dataKey={computeRowKey(f)}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            renderControl={props => (
              <EquipmentSelect
                {...props}
                projectId={project.id}
                name="equipments[]"
                placeholder={getLabel("equipments")}
                allowEquipmentCreation={canUserModifyCategories}
                entry={{ equipmentIds: f.equipments.map(e => e.equipment.id) }}
                setDirty={setDirty}
              />
            )}
            showEmptyMark
          >
            {equipmentNames(f)}
          </TableEditableCell>
        ),
        sortable: true,
      },
      equipmentType: {
        label: getLabel("equipmentType"),
        hidden: hidden("equipmentType"),
        minWidth: "250px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="equipmentType"
            dataKey={computeRowKey(f)}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            renderControl={props => (
              <EquipmentTypeSelect
                {...props}
                name="equipmentType"
                placeholder={getLabel("equipmentType")}
                setDirty={setDirty}
                allowEquipmentTypeCreation={canUserModifyGlobalCategories}
                entry={f}
              />
            )}
            showEmptyMark
          >
            {f.equipmentTypeId ?? ""}
          </TableEditableCell>
        ),
        sortable: true,
      },
      suspectedElements: {
        label: getLabel("suspectedElements"),
        hidden: hidden("suspectedElements"),
        minWidth: "250px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="suspectedElements"
            dataKey={computeRowKey(f)}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            renderControl={props => (
              <SuspectedElementSelect
                {...props}
                projectId={project.id}
                entry={f}
                allowSuspectedElementCreation={canUserModifyCategories}
                name="suspectedElements[]"
                placeholder={getLabel("suspectedElements")}
                setDirty={setDirty}
              />
            )}
            showEmptyMark
          >
            {suspectedElementNames(f)}
          </TableEditableCell>
        ),
        sortable: true,
      },
      unit: {
        label: getLabel("unit"),
        hidden: hidden("unit"),
        minWidth: "90px",
        render: f => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="unit"
            dataKey={f.id}
            edited={f.edited}
            setEditedDataKey={setEditedDataKey}
            renderControl={props => (
              <TableInputControl
                {...props}
                defaultValue={f.unit || ""}
                validations={[nonEmptyStringValidation]}
                setDirty={setDirty}
                placeholder={getLabel("unit")}
                maxLength={MAX_FAULT_UNIT_LENGTH}
              />
            )}
            showEmptyMark
          >
            {f.enabled ? f.unit : <NonValidated>{f.unit}</NonValidated>}
          </TableEditableCell>
        ),
        sortable: true,
      },
      min: {
        label: getLabel("min"),
        hidden: hidden("min"),
        minWidth: "100px",
        alignment: "right",
        render: f => {
          const s = formatNumber(f.min, { precision: 2 });
          return f.enabled ? s : <NonValidated>{s}</NonValidated>;
        },
        sortable: true,
      },
      max: {
        label: getLabel("max"),
        hidden: hidden("max"),
        minWidth: "100px",
        alignment: "right",
        render: f => {
          const s = formatNumber(f.max, { precision: 2 });
          return f.enabled ? s : <NonValidated>{s}</NonValidated>;
        },
        sortable: true,
      },
      separator: {
        hidden: filteringObservables,
        verticalSeparator: getLabel("separator"),
        showEmptyMark: false,
        alignment: "center",
        sticky: FIRST_STICKY_COLUMN_WIDTH,
        stickyBorder: false,
      },
      amplitude: {
        label: getLabel("amplitude"),
        hidden: hidden("amplitude"),
        minWidth: "150px",
        alignment: "right",
        render: f => {
          const s = formatNumber(f.amplitude, { precision: 2 });
          return f.enabled ? s : <NonValidated>{s}</NonValidated>;
        },
        sortable: true,
      },
    };

    const staticMetadata: { [K in Column]: ColumnMetaData<EditableFailure> } = {
      displayName: {
        label: getLabel("displayName"),
        render: f => (
          <>
            <TableEditableCell<Column>
              enabled={canUserEditSetup}
              column="displayName"
              dataKey={f.id}
              edited={f.edited}
              setEditedDataKey={setEditedDataKey}
              renderControl={props => (
                <TableInputControl
                  {...props}
                  defaultValue={f.displayName}
                  setDirty={setDirty}
                  placeholder={getLabel("displayName")}
                  validations={[
                    nonEmptyStringValidation,
                    uniqueDisplayNameValidation(failures, f.id),
                  ]}
                  maxLength={MAX_DISPLAY_NAME_LENGTH}
                />
              )}
              showEmptyMark
            >
              <FailureComparisonCheckbox
                checked={selectedFailureIds.includes(f.id)}
                onToggle={() => toggleFailureSelection(f.id)}
              />
              {canUserEditSetup && (
                <StyledFailureRadioButton checked={f.enabled} readOnly onClick={toggleFailure(f)} />
              )}
              <Name>
                <TooltipV2
                  placement="top"
                  overlay={<OverlayWrapper text={t("setup.diagnosis.failure.tooltip.color")} />}
                >
                  <ColorIndicator color={getFailureColor(project, "FAILURE", f.name)} />
                </TooltipV2>
                <span title={f.displayName}>
                  {f.enabled ? f.displayName : <NonValidated>{f.displayName}</NonValidated>}
                </span>
              </Name>
              {!f.edited && canUserEditSetup ? (
                <StyledButtonIconV2
                  onClick={() => setEditedDataKey(computeRowKey(f))}
                  className={HIDE_ON_EXPORT}
                >
                  <PencilLine size="m" color={color.carbon800} />
                </StyledButtonIconV2>
              ) : null}
            </TableEditableCell>
          </>
        ),
        hidden: hidden("displayName"),
        sticky: true,
        minWidth: `${FIRST_STICKY_COLUMN_WIDTH}px`,
        sortable: true,
      },
      technicalName: {
        label: getLabel("technicalName"),
        render: f => (
          <>
            <NameWithoutMargin>
              <span title={f.name}>
                {f.enabled ? f.name : <NonValidated>{f.name}</NonValidated>}
              </span>
            </NameWithoutMargin>
          </>
        ),
        hidden: hidden("technicalName"),
        minWidth: `${FIRST_STICKY_COLUMN_WIDTH}px`,
        sortable: true,
      },
      ...baseMetadata,
    };

    const dynamicMetadata: { [key in Column | string]: ColumnMetaData<Failure> } = staticMetadata;

    if (columnsFromImpacts) {
      const filteredColumnsFromImpacts = columnsFromImpacts
        .filter(obs =>
          filteringObservables
            ? activeObservableIds.includes(obs.id)
            : displayQuantitiesNotUsed || obs.enabled,
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .sort((a, b) => {
          if (a.qoiOrder === b.qoiOrder) return 0;
          if (a.qoiOrder === null) return 1;
          if (b.qoiOrder === null) return -1;
          return a.qoiOrder - b.qoiOrder;
        });

      for (const column of filteredColumnsFromImpacts) {
        const isObservable = observables.some(obs => obs.quantityId === column.id);
        const isQoi = column.qoiOrder !== null;

        dynamicMetadata[column.name] = {
          verticalAlignmentCenter: true,
          label: (
            <OBSColumn title={`${column.name} (${column.unit})`}>
              {isObservable && !isQoi ? (
                <ColumnHeaderWrapper>
                  <StyledBadge className={classNames(OBERVABLE_CN, !column.enabled && DISABLED_CN)}>
                    <StyledTooltip
                      placement="top"
                      overlay={
                        <OverlayWrapper
                          text={
                            column.enabled
                              ? t(
                                  "setup.paragraphs.failures.table.definitions.column.roles.observable.tooltip",
                                )
                              : t(
                                  "setup.paragraphs.failures.table.definitions.column.roles.observable.disabled.tooltip",
                                )
                          }
                        />
                      }
                    >
                      <span>
                        {t(
                          "setup.paragraphs.failures.table.definitions.column.roles.observable.label",
                        )}
                      </span>
                    </StyledTooltip>
                  </StyledBadge>
                  {column.name} ({column.unit})
                </ColumnHeaderWrapper>
              ) : (
                <ColumnHeaderWrapper>
                  {isQoi && (
                    <StyledBadge className={QOI_CN}>
                      <StyledTooltip
                        placement="top"
                        overlay={
                          <OverlayWrapper
                            text={t(
                              "setup.paragraphs.failures.table.definitions.column.roles.quantity_of_interest.tooltip",
                            )}
                          />
                        }
                      >
                        <span>
                          {t(
                            "setup.paragraphs.failures.table.definitions.column.roles.quantity_of_interest.label",
                          )}
                        </span>
                      </StyledTooltip>
                    </StyledBadge>
                  )}
                  {isObservable && (
                    <StyledBadge
                      className={classNames(OBERVABLE_CN, !column.enabled && DISABLED_CN)}
                    >
                      <StyledTooltip
                        placement="top"
                        overlay={
                          <OverlayWrapper
                            text={
                              column.enabled
                                ? t(
                                    "setup.paragraphs.failures.table.definitions.column.roles.observable.tooltip",
                                  )
                                : t(
                                    "setup.paragraphs.failures.table.definitions.column.roles.observable.disabled.tooltip",
                                  )
                            }
                          />
                        }
                      >
                        <span>
                          {t(
                            "setup.paragraphs.failures.table.definitions.column.roles.observable.label",
                          )}
                        </span>
                      </StyledTooltip>
                    </StyledBadge>
                  )}
                  {column.name} ({column.unit})
                </ColumnHeaderWrapper>
              )}
            </OBSColumn>
          ),
          render(f) {
            const impact = f.impacts.find(i => i.quantityId === column.id);
            const value = impact ? formatNumber(impact.value, { precision: 2, sign: true }) : null;
            return f.enabled && column.enabled ? value : <NonValidated>{value}</NonValidated>;
          },
          width: "100px",
          alignment: "center",
          diagonalLabel: true,
        };
      }
    }
    dynamicMetadata["buffer"] = {
      width: "150px",
      showEmptyMark: false,
    };

    return dynamicMetadata as typeof staticMetadata;
  }, [
    hidden,
    filteringObservables,
    activeObservableIds,
    columnsFromImpacts,
    canUserEditSetup,
    setEditedDataKey,
    toggleFailure,
    project,
    setDirty,
    failures,
    canUserModifyCategories,
    canUserModifyGlobalCategories,
    translations,
    optionsReparabilities,
    equipmentNames,
    suspectedElementNames,
    displayQuantitiesNotUsed,
    observables,
    selectedFailureIds,
    toggleFailureSelection,
  ]);

  const handleFormClose = React.useCallback(() => setEditedDataKey(undefined), [setEditedDataKey]);
  const openImportModal = React.useCallback(() => openModal("failuresImport"), [openModal]);
  const openDeleteModal = React.useCallback(() => openModal("failuresDelete"), [openModal]);
  const handleSubmit = React.useCallback(
    async (values: FormSignature, failure: Failure) => {
      await updateFailures({
        body: [
          {
            ...failure,
            description: values.description ?? null,
            unit: values.unit ?? null,
            systemId: values.system || null,
            equipmentTypeId: values.equipmentType || null,
            degradationTypeId: values.degradationType || null,
            failureCategoryId: values.failureCategory || null,
            equipmentIds: values.equipments ?? [],
            suspectedElementIds: values.suspectedElements ?? [],
            reparability: values?.reparability ? (values?.reparability as Reparability) : null,
            displayName: values.displayName ?? failure.name,
          },
        ],
        routeParams: { projectId: project.id, setupId: setup.id },
      });
    },
    [project.id, setup.id, updateFailures],
  );
  const allFailuresNotActivated = React.useMemo(() => failures.some(f => !f.enabled), [failures]);
  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  if (failures.length === 0)
    return (
      <Card>
        <StyledBlankStateV2
          title={t("setup.paragraphs.failures.table.failures.no_failures.blank_state.title")}
          subtitle={
            canUserEditSetup
              ? t(
                  "setup.paragraphs.failures.table.failures.no_failures.blank_state.no_quantities.text",
                )
              : null
          }
          onClick={canUserEditSetup ? openImportModal : undefined}
          buttonLabel={
            canUserEditSetup
              ? t("setup.paragraphs.failures.table.failures.no_failures.blank_state.button")
              : undefined
          }
        />
      </Card>
    );

  return (
    <>
      <div className={FailureTableActions}>
        <StyledSelector>
          <Selector options={selectorMenuOptions} value={option} onChange={setOption} />
        </StyledSelector>{" "}
      </div>
      <TableCard
        className={cx(TableCardWithoutBorder, TableHeaderNoPaddingT, backgroundColorCss)}
        title=""
        headerLeft={
          <>
            <SearchInputV2
              className={option === "matrix" ? "" : hiddenCss}
              placeholder={t("commons.placeholder.search")}
              value={search}
              onChange={handleSearchChange}
            />
            {option === "matrix" && (
              <>
                <FilterSelect
                  className={observableFilterSelect}
                  title={t("setup.diagnosis.failure.filter_observables")}
                  options={observableOptions}
                  filtered
                  values={activeObservableIds}
                  setValues={setSelectedObservableIds}
                />
                <Reset
                  filterValues={{ observables: activeObservableIds }}
                  setFilterValues={({ observables }) => setSelectedObservableIds(observables)}
                  defaultFilterValues={{ observables: [] }}
                />
              </>
            )}
            {allFailuresNotActivated && (
              <Switch
                className={option === "matrix" ? "" : hiddenCss}
                checked={displayDisabled}
                readOnly
                onChange={handleDisabledChanged}
              >
                {t("setup.diagnosis.failure.show_disabled")}
              </Switch>
            )}
            {notAllQuantitiesUsed && (
              <Switch
                className={option === "matrix" && !filteringObservables ? "" : hiddenCss}
                checked={displayQuantitiesNotUsed}
                readOnly
                onChange={handleNotUsedChanged}
              >
                {t("setup.diagnosis.failure.show_unused")}
              </Switch>
            )}
          </>
        }
        headerRight={
          <>
            <StyledInlineMenu
              className={option === "matrix" && !filteringObservables ? "" : hiddenCss}
              anchor="right"
              opened={menuOpened}
              setOpened={setMenuOpened}
              caret
              overlay={columns.map(column => (
                <Option
                  key={column}
                  removed={column === "separator"}
                  checkbox
                  disabled={requiredVisibleColumns.has(column)}
                  selected={visibleColumns.includes(column)}
                  onClick={() =>
                    setVisibleColumns(
                      visibleColumns.includes(column)
                        ? visibleColumns.filter(c => c !== column)
                        : [...visibleColumns, column],
                    )
                  }
                >
                  {columns.includes(column) ? getLabel(column) : column}
                </Option>
              ))}
            >
              <Link>{t("commons.select.manage_columns")}</Link>
            </StyledInlineMenu>
            <FaultComparisonButton
              mode={option}
              onClick={() => setComparisonOpen(true)}
              selectedFaultCount={selectedFaultCount}
            />
            {canUserEditSetup ? (
              <Menu
                arrowWidth={0}
                // gap={16}
                placement="bottom-end"
                overlay={
                  <>
                    <OptionGroup>
                      <ExportArtifactOption
                        projectId={project.id}
                        setupId={setup.id}
                        section="fault"
                      />
                      <Option closeOnClick onClick={openImportModal}>
                        <>
                          <UploadLine color={color.carbon800} />
                          {t("commons.message_card.replace")}
                        </>
                      </Option>
                      <Option closeOnClick onClick={openDeleteModal}>
                        <DeleteBinLine color={color.carbon800} />
                        {t("commons.message_card.delete")}
                      </Option>
                    </OptionGroup>
                  </>
                }
              >
                <ButtonIconV2>
                  <More2Fill size="m" />
                </ButtonIconV2>
              </Menu>
            ) : null}
          </>
        }
      >
        {option === "matrix" ? (
          <TableForm
            signature={FormSignature}
            findDataByDataKey={findDataById}
            editedDataKey={editedDataKey}
            onClose={handleFormClose}
            onSubmit={handleSubmit}
            dirty={dirty}
            setDirty={setDirty}
          >
            <Table
              stickyHeader
              loading={dataLoading}
              metadata={metadata}
              datas={sortedFilteredData}
              computeRowKey={computeRowKey}
              sort={sort}
              onSort={setSort}
            />
          </TableForm>
        ) : (
          <FaultFilterTable projectId={project.id} failures={failures} />
        )}
      </TableCard>
      {comparisonOpen && (
        <FaultSignatureComparisonModal
          onRequestClose={() => setComparisonOpen(false)}
          project={project}
          setups={setups}
          initialSetup={setup}
          selectedFailureIds={selectedFailureIds}
        />
      )}
    </>
  );
};

export default React.memo(FailuresTable);

//#region Css
const FailureTableActions = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  > button {
    padding: 8px;
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

const backgroundColorCss = css`
  background: ${color.titanium50};
`;

const OBSColumn = styled.div`
  display: flex;
  align-items: center;
  cursor: auto;
`;

const StyledTooltip = styled(TooltipV2)`
  max-width: 450px;
  text-overflow: ellipsis;
`;

const StyledFailureRadioButton = styled(RadioButton)`
  display: flex;
  align-items: center;
  height: 40px;

  input {
    border-width: 2px;
  }
`;

const failureSelectionInput = css`
  width: auto;
  margin-right: ${spacings.xs};
  z-index: 1;
`;

const ColumnHeaderWrapper = styled.span`
  display: flex;
  gap: 4px;
  overflow: hidden;
`;

const Name = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: 15px;
  margin-right: auto;
  overflow: hidden;

  /* Text */
  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: auto;
  }
`;

const NameWithoutMargin = styled(Name)`
  margin-left: 0;
`;

const ColorIndicator = styled.div<{ color: string }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  background-color: ${p => p.color};
`;

const NonValidated = styled.span`
  opacity: 0.3;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const observableFilterSelect = css`
  && {
    box-sizing: border-box;
    height: 39px;
  }
`;

const StyledInlineMenu = styled(InlineMenu)`
  margin-left: auto;
  font: ${fonts.body2};
`;

const hiddenCss = css`
  display: none;
`;

const StyledSelector = styled.div`
  margin-bottom: 16px;
`;

const StyledButtonIconV2 = styled(ButtonIconV2)`
  display: none;
  tr:hover & {
    display: flex;
  }
`;
const StyledBadge = styled(Badge)`
  align-items: center;
  color: ${color.carbon900};
  &.${OBERVABLE_CN} {
    background-color: ${color.lightNitrogen100};
    &.${DISABLED_CN} {
      opacity: 0.5;
    }
  }
  &.${QOI_CN} {
    background-color: ${color.oxygen100};
  }
`;

const MainText = styled.div`
  margin-bottom: 15px;
  font: ${fonts.body1};
`;
const Wrapper = styled.div`
  max-height: 150px;
`;
const StyledBlankStateV2 = styled(BlankStateV2)`
  padding: 25px 20px;
`;
//#endregion
